import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog';
import { NavButton } from '@/shared/components/NavButton';
import { PillButton } from '@/shared/components/PillButton';
import { TextInput } from '@/shared/components/TextInput';
import { DialogHeading, PlayerInvitationSection, CountdownSection } from './DialogShared';
import { getGameTypeButtonConfig } from '@/shared/utils/gameTypeHelpers';
import type { PlayerPresence, RoomGameType } from 'shared-types';

interface JoinRoomDialogProps {
  isOpen: boolean;
  onClose: () => void;
  gameType: RoomGameType | null;
  invitationCode: string;
  onInvitationCodeChange: (code: string) => void;
  onJoinRoom: () => void;
  players?: PlayerPresence[];
  error?: string | null;
  isLoading?: boolean;
  countdown?: number | null;
  isStarting?: boolean;
  isCreator?: boolean;
  onStartNow?: () => void;
}

export function JoinRoomDialog({
  isOpen,
  onClose,
  gameType,
  invitationCode,
  onInvitationCodeChange,
  onJoinRoom,
  players = [],
  error = null,
  isLoading = false,
  countdown = null,
  isStarting = false,
  isCreator = false,
  onStartNow = () => {
    // No-op default function
  }
}: JoinRoomDialogProps) {
  const { t } = useTranslation();

  // Get button configuration based on game type (handles null state)
  const { variant: buttonVariant, textKey } = getGameTypeButtonConfig(gameType);
  const buttonText = t(textKey);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} variant="green" title={t('landing.playWithFriends')}>
      <div className="flex flex-col gap-6">
        {/* Section 1: Game Mode (read-only - determined by room creator) */}
        <div className="flex flex-col gap-3">
          <DialogHeading>{t('landing.gameMode')}</DialogHeading>
          <NavButton variant={buttonVariant} disabled className="w-full h-12">
            {buttonText}
          </NavButton>
        </div>

        {/* Section 2: Invitation Code Input */}
        <div className="flex flex-col gap-2">
          <div className="flex gap-3 items-center">
            <TextInput
              variant="grey"
              placeholder={t('lobby.code')}
              value={invitationCode}
              onChange={(e) => {
                onInvitationCodeChange(e.target.value.toUpperCase());
              }}
              maxLength={6}
              disabled={isLoading}
              className="flex-1 h-12"
            />
            <PillButton
              variant="green"
              onClick={onJoinRoom}
              disabled={invitationCode.length !== 6 || isLoading}
              className="w-12 h-12"
            >
              {isLoading ? '...' : t('lobby.go')}
            </PillButton>
          </div>
          {/* Error message display */}
          {error && <p className="text-red-500 text-sm text-center font-semibold">{error}</p>}
        </div>

        {/* Section 3: Countdown or Player Invitations */}
        {isStarting && countdown !== null ? (
          <CountdownSection countdown={countdown} isCreator={isCreator} onStartNow={onStartNow} />
        ) : (
          <PlayerInvitationSection players={players} />
        )}
      </div>
    </Dialog>
  );
}
